import { supabase } from '@/lib/supabase';

export interface AdminUser {
  id: string;
  email?: string;
  created_at: string;
  last_sign_in_at?: string;
  outfits_count?: number;
  saved_hauls_count?: number;
}

export const getUsers = async (): Promise<AdminUser[]> => {
  // Try to get users from auth admin API first
  try {
    const { data, error } = await supabase.auth.admin.listUsers();
    if (!error && data?.users) {
      const users: AdminUser[] = data.users.map(u => ({
        id: u.id,
        email: u.email,
        created_at: u.created_at,
        last_sign_in_at: u.last_sign_in_at,
      }));

      // Enrich with outfit counts
      await enrichUserStats(users);
      return users;
    }
  } catch {
    // Admin API not available, fallback to outfits table
  }

  // Fallback: get users from outfits table
  const { data: outfits, error: outfitsError } = await (supabase as any)
    .from('outfits')
    .select('user_id, user_name, user_avatar, created_at');

  if (outfitsError) throw outfitsError;

  const userMap = new Map<string, AdminUser>();
  (outfits || []).forEach((o: any) => {
    if (!userMap.has(o.user_id)) {
      userMap.set(o.user_id, {
        id: o.user_id,
        email: o.user_name || undefined,
        created_at: o.created_at,
        outfits_count: 0,
      });
    }
    userMap.get(o.user_id)!.outfits_count = (userMap.get(o.user_id)!.outfits_count || 0) + 1;
  });

  return Array.from(userMap.values());
};

const enrichUserStats = async (users: AdminUser[]): Promise<void> => {
  if (users.length === 0) return;

  const userIds = users.map(u => u.id);

  // Get outfit counts
  const { data: outfitCounts } = await (supabase as any)
    .from('outfits')
    .select('user_id')
    .in('user_id', userIds);

  if (outfitCounts) {
    const countMap = new Map<string, number>();
    (outfitCounts as any[]).forEach((o: any) => {
      countMap.set(o.user_id, (countMap.get(o.user_id) || 0) + 1);
    });
    users.forEach(u => {
      u.outfits_count = countMap.get(u.id) || 0;
    });
  }

  // Try to get saved hauls count from hauls_saves table
  try {
    const { data: haulsSaves } = await (supabase as any)
      .from('hauls_saves')
      .select('user_id')
      .in('user_id', userIds);

    if (haulsSaves) {
      const saveMap = new Map<string, number>();
      (haulsSaves as any[]).forEach((h: any) => {
        saveMap.set(h.user_id, (saveMap.get(h.user_id) || 0) + 1);
      });
      users.forEach(u => {
        u.saved_hauls_count = saveMap.get(u.id) || 0;
      });
    }
  } catch {
    // hauls_saves table may not exist
  }
};

export const getUserOutfits = async (userId: string) => {
  const { data, error } = await (supabase as any)
    .from('outfits')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const deleteUserOutfits = async (userId: string): Promise<number> => {
  const { data, error } = await (supabase as any)
    .from('outfits')
    .delete()
    .eq('user_id', userId)
    .select('id');

  if (error) throw error;
  return (data || []).length;
};
